import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { Container, Chapter, FadeUp } from "./primitives";

const TIERS = [
  {
    id: "starter",
    name: "Starter",
    price: { m: 29, y: 24 },
    tag: "For solo operators",
    features: [
      "500 leads / month",
      "1,500 AI credits",
      "Scheduler (basic)",
      "CRM board — 1 pipeline",
      "1 team member",
      "Email support",
    ],
    limits: ["No approval workflow", "No API"],
  },
  {
    id: "pro",
    name: "Professional",
    price: { m: 89, y: 74 },
    tag: "Most popular",
    popular: true,
    features: [
      "Unlimited leads",
      "15,000 AI credits",
      "Scheduler (advanced)",
      "CRM board — 5 pipelines",
      "5 team members",
      "Approval workflow",
      "Automation",
      "Priority support",
    ],
  },
  {
    id: "biz",
    name: "Business",
    price: { m: 199, y: 169 },
    tag: "For scaling teams",
    features: [
      "Unlimited leads",
      "50,000 AI credits",
      "Multi-account (5)",
      "CRM — unlimited pipelines",
      "20 team members",
      "Approval + audit logs",
      "API access",
      "SSO / SAML",
      "Priority support",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: { m: "Custom", y: "Custom" },
    tag: "For orgs & compliance",
    features: [
      "Unlimited everything",
      "Custom AI credits",
      "Unlimited accounts",
      "Custom pipelines",
      "Unlimited team members",
      "Dedicated CSM",
      "SLAs, DPA, data residency",
      "Private deployment",
    ],
  },
];

export default function Pricing() {
  const [yearly, setYearly] = useState(true);

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-32 md:py-40 border-t border-white/10">
      <div className="orb bg-[#7C3AED] w-[560px] h-[560px] -top-40 right-[-10%] opacity-20" />
      <Container className="relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <Chapter
            number="09"
            kicker="Pricing"
            title={<>Simple pricing. <span className="font-serif-x italic">Serious</span> outcomes.</>}
            description="Start free, scale as you close. Every plan includes AI Discovery, CRM, Drafts and the Approval workflow."
          />

          <FadeUp>
            <div
              data-testid="pricing-toggle"
              className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-1"
            >
              <button
                data-testid="pricing-toggle-monthly"
                onClick={() => setYearly(false)}
                className={`relative px-4 py-2 text-sm rounded-full transition-colors ${!yearly ? "bg-white text-black" : "text-white/70 hover:text-white"}`}
              >
                Monthly
              </button>
              <button
                data-testid="pricing-toggle-yearly"
                onClick={() => setYearly(true)}
                className={`relative px-4 py-2 text-sm rounded-full transition-colors flex items-center gap-2 ${yearly ? "bg-white text-black" : "text-white/70 hover:text-white"}`}
              >
                Yearly
                <span className={`text-[10px] font-mono-x px-1.5 py-0.5 rounded ${yearly ? "bg-black/10 text-black" : "bg-emerald-400/15 text-emerald-300"}`}>
                  −20%
                </span>
              </button>
            </div>
          </FadeUp>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {TIERS.map((t, i) => {
            const price = yearly ? t.price.y : t.price.m;
            return (
              <FadeUp key={t.id} delay={i * 0.05}>
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`pricing-card-${t.id}`}
                  className={`relative rounded-3xl border p-7 md:p-8 h-full flex flex-col ${
                    t.popular
                      ? "border-white/25 bg-gradient-to-b from-[#4F46E5]/15 via-[#0F172A]/60 to-transparent ring-glow"
                      : "border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors"
                  }`}
                >
                  {t.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-black text-[10px] font-mono-x tracking-[0.2em] uppercase">
                      <Sparkles className="h-3 w-3" /> Most popular
                    </span>
                  )}
                  <div className="text-[10px] font-mono-x tracking-[0.22em] uppercase text-white/40">{t.tag}</div>
                  <div className="mt-1 font-display text-2xl">{t.name}</div>
                  <div className="mt-6 flex items-baseline gap-1">
                    {typeof price === "number" ? (
                      <>
                        <AnimatePresence mode="wait">
                          <motion.span
                            key={price}
                            initial={{ y: 12, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -8, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="font-display text-5xl tracking-[-0.03em]"
                          >
                            ${price}
                          </motion.span>
                        </AnimatePresence>
                        <span className="text-white/40 text-sm">/mo</span>
                      </>
                    ) : (
                      <span className="font-display text-4xl tracking-[-0.03em]">Custom</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs font-mono-x text-white/40">
                    {typeof price === "number" ? (yearly ? "billed annually" : "billed monthly") : "let’s talk"}
                  </div>

                  <ul className="mt-6 space-y-2.5">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-white/75">
                        <Check className="h-4 w-4 mt-0.5 text-emerald-300 shrink-0" strokeWidth={2.5} />
                        {f}
                      </li>
                    ))}
                    {t.limits?.map((l) => (
                      <li key={l} className="flex items-start gap-2.5 text-sm text-white/35">
                        <span className="h-4 w-4 mt-0.5 shrink-0 flex items-center justify-center">·</span>
                        {l}
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#contact"
                    data-testid={`pricing-cta-${t.id}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-sm font-medium transition-colors ${
                      t.popular ? "bg-white text-black hover:bg-white/90" : "border border-white/15 bg-white/[0.03] hover:bg-white/[0.06] text-white"
                    }`}
                  >
                    {t.id === "enterprise" ? "Talk to sales" : "Start free trial"}
                  </a>
                </motion.div>
              </FadeUp>
            );
          })}
        </div>

        <FadeUp delay={0.2}>
          <div className="mt-14 text-center text-sm text-white/50">
            All plans start with a <span className="text-white">14-day free trial</span>. No credit card required.
          </div>
        </FadeUp>
      </Container>
    </section>
  );
}
